import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAdminProfile } from "../../features/adminAuth/adminAuthStorage";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.verixxon.com";

const formatMoney = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const QUICK_LINKS = [
  {
    label: "Users",
    sub: "Manage registered accounts",
    to: "/admin/users",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    ),
  },
  {
    label: "Inventory",
    sub: "OTP providers & rates",
    to: "/admin/inventory",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8M12 17v4" />
      </svg>
    ),
  },
  {
    label: "Transactions",
    sub: "Payment & wallet ledger",
    to: "/admin/transactions",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    label: "Orders",
    sub: "OTP order history",
    to: "/admin/orders",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 12h6M9 16h4" />
      </svg>
    ),
  },
  {
    label: "Blog",
    sub: "Articles & announcements",
    to: "/admin/blog",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
  },
  {
    label: "Legal",
    sub: "Terms, privacy & policies",
    to: "/admin/legal",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
];

const AdminDashboardPage = () => {
  const profile = getAdminProfile();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setLoading(true);
      try {
        const [usersRes, ordersRes, txRes, providersRes] = await Promise.allSettled([
          fetch(`${API_BASE_URL}/api/user/auth/users`),
          fetch(`${API_BASE_URL}/api/admin/orders`),
          fetch(`${API_BASE_URL}/api/admin/orders/transactions`),
          fetch(`${API_BASE_URL}/api/admin/providers`),
        ]);

        const safe = async (settled) => {
          if (settled.status !== "fulfilled" || !settled.value.ok) return null;
          try { return await settled.value.json(); } catch { return null; }
        };

        const [usersPayload, ordersPayload, txPayload, providersPayload] = await Promise.all([
          safe(usersRes),
          safe(ordersRes),
          safe(txRes),
          safe(providersRes),
        ]);

        if (!cancelled) {
          const orderTotals = ordersPayload?.totals || {};
          const txData = Array.isArray(txPayload?.data) ? txPayload.data : [];
          const isCompletedTopup = (transaction) => {
            const status = String(transaction?.status || "").toLowerCase();
            return (
              transaction?.category === "wallet_topup" &&
              (status === "completed" || status === "success" || status === "paid")
            );
          };

          const totalDeposits = txData
            .filter((t) => isCompletedTopup(t))
            .reduce((sum, t) => sum + Number(t.amount || 0), 0);

          const totalPurchased = txData
            .filter((t) => t.category === "otp_purchase")
            .reduce((sum, t) => sum + Number(t.amount || 0), 0);

          const totalRefundedBack = txData
            .filter((t) => t.category === "otp_refund")
            .reduce((sum, t) => sum + Number(t.amount || 0), 0);

          setStats({
            userCount: Array.isArray(usersPayload?.data) ? usersPayload.data.length : null,
            orderCount: orderTotals.total ?? null,
            activeOrders: orderTotals.waiting ?? null,
            refundedOrders: orderTotals.refunded ?? null,
            totalSales: orderTotals.totalSales ?? null,
            totalRefunds: orderTotals.totalRefunds ?? null,
            txCount: txData.length > 0 ? txData.length : (txPayload ? 0 : null),
            providerCount: Array.isArray(providersPayload?.data) ? providersPayload.data.length : null,
            totalDeposits: txPayload ? totalDeposits : null,
            netUserSpend: txPayload ? Math.max(0, totalPurchased - totalRefundedBack) : null,
          });
        }
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStats();
    return () => { cancelled = true; };
  }, []);

  const statCards = [
    {
      label: "Total Users",
      value: stats?.userCount,
      color: "teal",
    },
    {
      label: "Total Orders",
      value: stats?.orderCount,
      color: "teal",
    },
    {
      label: "Active Orders",
      value: stats?.activeOrders,
      color: "amber",
    },
    {
      label: "Refunded Orders",
      value: stats?.refundedOrders,
      color: "blue",
    },
    {
      label: "Gross Sales",
      value: stats?.totalSales != null ? formatMoney(stats.totalSales) : null,
      color: "green",
    },
    {
      label: "Total Refunds",
      value: stats?.totalRefunds != null ? formatMoney(stats.totalRefunds) : null,
      color: "red",
    },
    {
      label: "Transactions",
      value: stats?.txCount,
      color: "teal",
    },
    {
      label: "OTP Providers",
      value: stats?.providerCount,
      color: "teal",
    },
    {
      label: "Total Deposits",
      value: stats?.totalDeposits != null ? formatMoney(stats.totalDeposits) : null,
      color: "green",
    },
    {
      label: "Net User Spend",
      value: stats?.netUserSpend != null ? formatMoney(stats.netUserSpend) : null,
      color: "teal",
    },
  ];

  const displayValue = (raw) => {
    if (loading) return <span className="dash-stat-loading">—</span>;
    if (raw == null) return <span className="dash-stat-loading">N/A</span>;
    return raw;
  };

  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <section className="admin-page-card">
      <div className="dash-header">
        <div>
          <h2 className="dash-title">Dashboard</h2>
          <p className="dash-subtitle">
            {greeting}, <strong>{profile?.name || "Admin"}</strong>. Here's a live overview of your platform.
          </p>
        </div>
        <div className="dash-date">
          {now.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </div>
      </div>

      <div className="dash-stats-grid">
        {statCards.map((card) => (
          <div key={card.label} className={`dash-stat-card dash-stat-${card.color}`}>
            <span className="dash-stat-label">{card.label}</span>
            <strong className="dash-stat-value">{displayValue(card.value)}</strong>
          </div>
        ))}
      </div>

      <div className="dash-section-label">Quick Navigation</div>

      <div className="dash-quick-grid">
        {QUICK_LINKS.map((item) => (
          <Link key={item.to} to={item.to} className="dash-quick-card">
            <span className="dash-quick-icon">{item.icon}</span>
            <span className="dash-quick-text">
              <span className="dash-quick-label">{item.label}</span>
              <span className="dash-quick-sub">{item.sub}</span>
            </span>
            <span className="dash-quick-arrow">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default AdminDashboardPage;
